import Accordion from '@zenbusiness/zui/Accordion';
import AccordionDetails from '@zenbusiness/zui/AccordionDetails';
import AccordionSummary from '@zenbusiness/zui/AccordionSummary';
import Heading from '@zenbusiness/zui/Heading';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { ZB_BANKING_SUNSET_PH3_HIGH_RISK_CUSTOMERS } from '#client/constants/experimentsFlags';
import useFeatureFlag from '#client/hooks/useFeatureFlag';

const DEFAULT_DATES = Object.freeze({ closeDate: 'November 30', depositCutoffDate: 'November 1' });

// High-risk customers have their accounts closed on Oct 2 instead of Nov 30.
const HIGH_RISK_DATES = Object.freeze({ closeDate: 'October 2', depositCutoffDate: 'October 2' });

/**
 * Builds the wind-down questions for the customer's closure dates.
 * @param {{ closeDate: string, depositCutoffDate: string }} dates The account closure and deposit cutoff dates
 * @returns {{ question: string, answers: string[] }[]} The FAQs
 */
const getFaqs = ({ closeDate, depositCutoffDate }) => [
  {
    question: 'Why is ZenBusiness Banking closing?',
    answers: [
      'We\u2019ve identified other banking options that provide broad benefits, financial tools, and great support for growing businesses.',
      `As part of this transition, ZenBusiness Banking accounts will close on ${closeDate}. We\u2019re making this change so we can focus on improving your overall ZenBusiness experience while helping make your move to a new bank as smooth as possible.`
    ]
  },
  {
    question: 'What happens to my ZenBusiness Banking account?',
    answers: [
      `Your ZenBusiness Banking account will close on ${closeDate}.`,
      `Your account stays open and usable through the transition. On ${depositCutoffDate}, 2026, you\u2019ll no longer be able to add money to your account, though your existing balance and account history will still be visible.`
    ]
  },
  {
    question: 'Do I have to choose one of the banking partners identified by ZenBusiness?',
    answers: [
      'No. You can explore one of our preferred partners or move your funds to any financial institution you choose. Partner offers, features, eligibility requirements, and account approval may vary.'
    ]
  },
  {
    question: 'Am I guaranteed to be approved?',
    answers: [
      'No. Each provider has its own application, identity-verification, eligibility, and approval requirements. Applying with one of our partners doesn\u2019t guarantee approval, and you\u2019re always free to apply with a bank of your choice.'
    ]
  },
  {
    question: 'What happens if I don\u2019t take any action?',
    answers: [
      `If you haven\u2019t opted in or moved your funds by ${closeDate}, 2026, your ZenBusiness Banking account will close along with the rest of the program.`,
      'A check will be mailed to the address on file for any remaining balance greater than $1. Confirm that your mailing address is current to help prevent delays.'
    ]
  }
];

/**
 * The wind-down questions shown beneath the partner cards.
 * @returns {import('react').ReactElement} The FAQ section
 */
export default function BankingOptionsFaq() {
  const [isHighRiskCustomer] = useFeatureFlag(ZB_BANKING_SUNSET_PH3_HIGH_RISK_CUSTOMERS);
  const faqs = getFaqs(isHighRiskCustomer ? HIGH_RISK_DATES : DEFAULT_DATES);

  return (
    <Stack
      sx={{
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 3, md: 7 },
        alignItems: 'flex-start'
      }}
    >
      <Heading
        color="primary"
        size="xl"
        sx={{ flex: { md: 1 } }}
      >
        Frequently asked questions
      </Heading>
      <Stack sx={{ gap: 2, width: '100%', flex: { md: 2 } }}>
        {faqs.map(({ question, answers }, index) => (
          <Accordion
            disableGutters
            key={question}
            sx={{
              borderRadius: 1.5,
              border: (theme) => `1px solid ${theme.palette.border.main}`,
              '&::before': { display: 'none' }
            }}
          >
            <AccordionSummary
              aria-controls={`banking-options-faq-${index}-panel`}
              expandIcon={<ExpandMoreIcon />}
              id={`banking-options-faq-${index}-header`}
            >
              <Text
                color="primary"
                component="span"
                size="md"
                weight="medium"
              >
                {question}
              </Text>
            </AccordionSummary>
            <AccordionDetails id={`banking-options-faq-${index}-panel`}>
              <Stack sx={{ gap: 1.5 }}>
                {answers.map((answer) => (
                  <Text
                    color="secondary"
                    key={answer}
                    size="sm"
                  >
                    {answer}
                  </Text>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
    </Stack>
  );
}
